import { Trans as _Trans } from "../../lib/i18n/Trans";
import { Spinner } from "../../components/Spinner";
import { cn } from "../../lib/utils";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useWindowSize } from "../WindowUtility";
import { Cell } from "./cells";
import { shouldRenderColumn } from "./DataTable";
import { DATA_TABLE_ROW_HEIGHT_PX, DataTableLayoutContext, DEFAULT_DATA_TABLE_LAYOUT } from "./layout";
import { Body, Head, Header, Root, Row } from "./parts";
import { getDataTableColumnPresets } from "./presets";
const MIN_COLUMN_WIDTH = 40;

/**
 * @cc [owner:aubin-tchoi,label:product;react] minimal-columns
 * `columns` MUST contain only the columns the table's purpose requires; a new column MUST NOT be
 * added when its data fits in an existing column (e.g. as a secondary line or description) or
 * belongs in a details view.
 */
/**
 * @cc [owner:aubin-tchoi,label:product;react] header-never-clipped
 * In every supported locale (en-US, en-GB, fr-FR, de-DE), each rendered header label and its sort
 * icon MUST be fully visible, without truncation or overflow, at every viewport width where its
 * column is rendered per `columnsBreakpoints`.
 */
/**
 * Virtualized DataTable for large or infinite datasets: rows are windowed with
 * TanStack Virtual, with a sticky header and infinite loading via onLoadMore.
 * @summary Virtualized, infinitely scrollable data table.
 */
export function ScrollableDataTable({
  data,
  totalRowCount,
  columns,
  className,
  widthClassName = "w-full",
  columnsBreakpoints = {},
  maxHeight,
  onLoadMore,
  sorting,
  setSorting,
  isLoading = false,
  rowSelection,
  setRowSelection,
  enableRowSelection,
  enableMultiRowSelection = true,
  getRowId,
  containerRef,
  hideRowDivider = false,
  disableRowClickSelection = false,
  density = "default",
  getRowLabel
}) {
  const windowSize = useWindowSize();
  const loadMoreRef = useRef(null);
  const scrollSentinelRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(0);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const tableContainerRef = useRef(null);
  const isSorting = !!setSorting;
  const setRef = element => {
    tableContainerRef.current = element;
    if (containerRef) {
      if (typeof containerRef === "function") {
        containerRef(element);
      } else if ("current" in containerRef) {
        containerRef.current = element;
      }
    }
  };
  useEffect(() => {
    if (!tableContainerRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setTableWidth(entry.contentRect.width);
      }
    });
    resizeObserver.observe(tableContainerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const onSortingChange = sorting && setSorting ? updater => {
    const newValue = typeof updater === "function" ? updater(sorting) : updater;
    setSorting(newValue);
  } : undefined;
  const onRowSelectionChange = rowSelection && setRowSelection ? updater => {
    const newValue = typeof updater === "function" ? updater(rowSelection) : updater;
    setRowSelection(newValue);
  } : undefined;
  const table = useReactTable({
    data,
    columns,
    rowCount: totalRowCount,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    ...(enableRowSelection && {
      onRowSelectionChange
    }),
    state: {
      ...(enableRowSelection && {
        rowSelection
      }),
      ...(isSorting && {
        sorting
      })
    },
    manualSorting: isSorting,
    ...(isSorting && {
      onSortingChange: onSortingChange
    }),
    enableSortingRemoval: true,
    enableRowSelection,
    enableMultiRowSelection,
    getRowId,
    ...(getRowLabel && {
      meta: {
        getRowLabel
      }
    })
  });
  useEffect(() => {
    if (!tableContainerRef.current || !table || !tableWidth) {
      return;
    }
    const columns = table.getAllColumns();
    const idealSizing = columns.reduce((acc, column) => {
      var _column$columnDef$met;
      const ratio = ((_column$columnDef$met = column.columnDef.meta) === null || _column$columnDef$met === void 0 ? void 0 : _column$columnDef$met.sizeRatio) || 0;
      const calculated = Math.max(Math.floor(ratio / 100 * tableWidth), MIN_COLUMN_WIDTH);
      return {
        ...acc,
        [column.id]: calculated
      };
    }, {});
    const totalIdealWidth = Object.values(idealSizing).reduce((a, b) => a + b, 0);
    const widthDifference = tableWidth - totalIdealWidth;

    // Rounding leftovers go to the widest column.
    if (widthDifference !== 0) {
      const adjustColumnId = Object.entries(idealSizing).sort((a, b) => b[1] - a[1])[0][0];
      idealSizing[adjustColumnId] += widthDifference;
    }
    table.setColumnSizing(idealSizing);
  }, [table, tableWidth]);
  const columnSizing = table.getState().columnSizing;
  const {
    rows
  } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => DATA_TABLE_ROW_HEIGHT_PX[density]
  });
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!onLoadMore || !node) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !isLoading) {
        onLoadMore();
      }
    }, {
      root: tableContainerRef.current,
      rootMargin: "200% 0% 0% 0%",
      threshold: 0.1
    });
    observer.observe(node);
    return () => {
      observer.unobserve(node);
      observer.disconnect();
    };
  }, [onLoadMore, isLoading]);
  useEffect(() => {
    const sentinel = scrollSentinelRef.current;
    const root = tableContainerRef.current;
    if (!sentinel || !root) {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setCanScrollDown(!entry.isIntersecting), {
      root,
      threshold: 0.1
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);
  const layout = useMemo(() => ({
    ...DEFAULT_DATA_TABLE_LAYOUT,
    density
  }), [density]);
  return /*#__PURE__*/React.createElement(DataTableLayoutContext.Provider, {
    value: layout
  }, /*#__PURE__*/React.createElement("div", {
    className: cn("relative overflow-y-auto overflow-x-hidden", className, widthClassName, maxHeight === true ? "flex-1" : typeof maxHeight === "string" ? maxHeight : "max-h-100"),
    ref: setRef
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(Root, {
    className: "w-full table-fixed"
  }, /*#__PURE__*/React.createElement(Header, {
    className: "sticky top-0 z-20 bg-background shadow-sm"
  }, table.getHeaderGroups().map(headerGroup => /*#__PURE__*/React.createElement(Row, {
    key: headerGroup.id,
    widthClassName: widthClassName
  }, headerGroup.headers.map(header => {
    const breakpoint = columnsBreakpoints[header.id];
    if (!windowSize.width || !shouldRenderColumn(windowSize.width, breakpoint)) {
      return null;
    }
    const canSort = isSorting && header.column.getCanSort() && getDataTableColumnPresets(header.column).sortable;
    const headerContent = /*#__PURE__*/React.createElement("span", {
      className: "truncate"
    }, flexRender(header.column.columnDef.header, header.getContext()));
    return /*#__PURE__*/React.createElement(Head, {
      column: header.column,
      key: header.id,
      onSort: canSort ? header.column.getToggleSortingHandler() : undefined,
      className: "max-w-0",
      style: {
        width: columnSizing[header.id],
        minWidth: columnSizing[header.id]
      }
    }, canSort ? headerContent : /*#__PURE__*/React.createElement("div", {
      className: "flex w-full items-center gap-1 whitespace-nowrap"
    }, headerContent));
  })))), /*#__PURE__*/React.createElement(Body, {
    className: "relative w-full",
    style: {
      height: `${rowVirtualizer.getTotalSize()}px`
    }
  }, rowVirtualizer.getVirtualItems().map(virtualRow => {
    const row = rows[virtualRow.index];
    const handleRowClick = () => {
      var _row$original$onClick, _row$original;
      if (enableRowSelection && row.getCanSelect()) {
        row.toggleSelected(!enableMultiRowSelection ? true : undefined);
      }
      (_row$original$onClick = (_row$original = row.original).onClick) === null || _row$original$onClick === void 0 || _row$original$onClick.call(_row$original);
    };
    return /*#__PURE__*/React.createElement(Row, {
      key: row.id,
      id: row.id,
      widthClassName: widthClassName,
      hideBottomBorder: hideRowDivider,
      onClick: enableRowSelection && !disableRowClickSelection ? handleRowClick : row.original.onClick,
      onDoubleClick: row.original.onDoubleClick,
      rowData: row.original,
      className: "absolute w-full",
      ...(enableRowSelection && {
        "data-selected": row.getIsSelected()
      }),
      style: {
        transform: `translateY(${virtualRow.start}px)`
      }
    }, row.getVisibleCells().map(cell => {
      const breakpoint = columnsBreakpoints[cell.column.id];
      if (!windowSize.width || !shouldRenderColumn(windowSize.width, breakpoint)) {
        return null;
      }
      return /*#__PURE__*/React.createElement(Cell, {
        column: cell.column,
        key: cell.id,
        id: cell.id,
        className: "max-w-0",
        style: {
          width: columnSizing[cell.column.id],
          minWidth: columnSizing[cell.column.id]
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "flex items-center space-x-1"
      }, /*#__PURE__*/React.createElement("span", {
        className: "truncate"
      }, flexRender(cell.column.columnDef.cell, cell.getContext()))));
    }));
  }))), /*#__PURE__*/React.createElement("div", {
    ref: loadMoreRef,
    className: "absolute bottom-0 h-1 w-full"
  }), /*#__PURE__*/React.createElement("div", {
    ref: scrollSentinelRef,
    className: "h-px"
  })), /*#__PURE__*/React.createElement("div", {
    className: cn("pointer-events-none sticky -bottom-px left-0 right-0 -mt-10 h-10 bg-linear-to-t", "from-background via-background/60 to-transparent transition-opacity duration-300", canScrollDown ? "opacity-100" : "opacity-0")
  }), isLoading && /*#__PURE__*/React.createElement("div", {
    className: "sticky bottom-0 left-0 right-0 flex justify-center bg-background/80 py-2 backdrop-blur-sm"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2 text-sm text-muted-foreground"
  }, /*#__PURE__*/React.createElement(Spinner, {
    size: "xs"
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(_Trans,
  /** i18n */
  {
    id: "BEj-mu",
    message: "Loading more data\u2026"
  }))))));
}
//# sourceMappingURL=ScrollableDataTable.js.map